// The type's settings disclosure on a V2.9 Input row, generated from the schema file.
import { Checkbox } from '@neo/primitives/checkbox'
import { Input } from '@neo/primitives/input'
import { Select } from '@neo/primitives/select'
import { Typography } from '@neo/primitives/typography'
import { X } from 'lucide-react'
import { useState } from 'react'
import {
  applySetting,
  type SettingField,
  settingDefault,
  settingFieldsFor,
  settingValue,
} from './collection-configuration-config'
import type { CollectionConfig } from './collection-configuration-types'

// V2.5's own address presets, from its AddressFieldsEditor.
const ADDRESS_PRESETS = [
  { label: 'Full address', value: 'full' },
  { label: 'Country only', value: 'country' },
  { label: 'State only', value: 'state' },
  { label: 'City only', value: 'city' },
  { label: 'Location', value: 'location' },
]

/** V2.5's chip list: type an option, press enter. A call-supplied choice has an empty list. */
const ChoicesEditor = ({
  options,
  disabled,
  onChange,
}: {
  options: string[]
  disabled: boolean
  onChange: (next: string[] | undefined) => void
}) => {
  const [draft, setDraft] = useState('')
  const commit = () => {
    const value = draft.trim()
    if (!value || options.includes(value)) return
    onChange([...options, value])
    setDraft('')
  }
  return (
    <div className="flex min-h-[40px] flex-wrap items-center gap-1.5 rounded-md border border-st2 bg-background p-2">
      {options.map((option) => (
        <span
          key={option}
          className="inline-flex items-center gap-1 rounded bg-bg3 px-1.5 py-0.5 text-xs-400"
        >
          {option}
          <button
            type="button"
            data-testid="button-remove-choice"
            aria-label={`Remove option ${option}`}
            disabled={disabled}
            onClick={() => onChange(options.filter((item) => item !== option) as string[])}
            className="text-secondary hover:text-primary"
          >
            <X size={12} />
          </button>
        </span>
      ))}
      <input
        data-testid="input-add-choice"
        aria-label="Add a choice option"
        value={draft}
        disabled={disabled}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          }
        }}
        onBlur={commit}
        placeholder="Type an option and press Enter"
        className="min-w-[140px] flex-1 bg-transparent text-p-400 outline-none"
      />
    </div>
  )
}

const FieldEditor = ({
  field,
  type,
  value,
  disabled,
  name,
  onChange,
}: {
  field: SettingField
  type: string
  value: unknown
  disabled: boolean
  name: string
  onChange: (next: unknown) => void
}) => {
  if (field.name === 'choices')
    return (
      <ChoicesEditor
        options={Array.isArray(value) ? (value as string[]) : []}
        disabled={disabled}
        onChange={(next) => onChange(next?.length ? next : undefined)}
      />
    )
  if (field.name === 'required_fields')
    return (
      <div data-testid="select-address-fields">
        <Select
          name={name}
          options={ADDRESS_PRESETS}
          value={typeof value === 'string' ? value : 'full'}
          onChange={(next) => onChange(next)}
          searchable={false}
          virtualized={false}
          disabled={disabled}
          size="sm"
        />
      </div>
    )
  if (field.kind === 'integer' || field.kind === 'number')
    return (
      <Input
        type="number"
        min={1}
        step={field.kind === 'integer' ? 1 : 'any'}
        value={typeof value === 'number' ? String(value) : ''}
        disabled={disabled}
        placeholder="e.g. 4"
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))}
      />
    )
  // V2.5 renders every remaining toggle as a plain checkbox carrying its own default
  return (
    <Checkbox
      name={name}
      data-testid={`checkbox-setting-${field.name}`}
      label={field.label}
      disabled={disabled}
      value={typeof value === 'boolean' ? value : settingDefault(type, field.name)}
      onChange={(checked: boolean) => onChange(checked)}
    />
  )
}

export const CollectionTypeSettings = ({
  value,
  disabled,
  baseName,
  onChange,
}: {
  value?: CollectionConfig
  disabled: boolean
  baseName?: string
  onChange: (next: CollectionConfig) => void
}) => {
  const type = value?.type ?? ''
  const fields = settingFieldsFor(type)
  if (!fields.length) return null
  return (
    <div className="flex flex-col gap-2.5" data-testid="collection-type-settings">
      {fields.map((field) => {
        // V2.5 keeps a dependent setting off and locked until the one it needs is on
        const dependency = fields.find((f) => f.name === field.requires)
        const locked = !!dependency && settingValue(value, dependency) !== true
        const current = settingValue(value, field)
        const box = field.kind === 'boolean' && field.name !== 'required_fields'
        return (
          <div key={field.name} className="space-y-1">
            {!box && (
              <Typography variant="XS/600" className="text-secondary">
                {field.label}
              </Typography>
            )}
            <FieldEditor
              field={field}
              type={type}
              name={`${baseName}-${field.name}`}
              value={current}
              disabled={disabled || locked}
              onChange={(next) => onChange(applySetting(value, field, next))}
            />
          </div>
        )
      })}
    </div>
  )
}
